/**
 * Chart wrappers over recharts that follow the §13.3 tokens (colours via `--wd-*` custom properties) and keep
 * a text alternative: every chart carries a title, a description and a collapsed data table for screen readers
 * and for operators who prefer numbers. The plot itself is decorative for assistive technology: it sits under
 * `aria-hidden` and is kept out of the tab order (`SURFACE_PROPS`, E2E-02 `aria-hidden-focus`); the "Show as table"
 * disclosure is the keyboard-reachable equivalent.
 */
import { useIntl } from '@windrop/ui';
import { useId, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { NoDataYet } from './EmptyState';

export const CHART_COLORS = {
  primary: 'var(--wd-action)',
  secondary: 'var(--wd-sponsor-accent)',
  prize: 'var(--wd-prize-accent)',
  success: 'var(--wd-success)',
  warning: 'var(--wd-warning)',
  error: 'var(--wd-error)',
  grid: 'var(--wd-border)',
  text: 'var(--wd-text-secondary)',
} as const;

export interface Series {
  key: string;
  label: string;
  color?: keyof typeof CHART_COLORS;
}

export interface ChartProps<Row extends Record<string, unknown>> {
  title: string;
  description?: string;
  data: Row[];
  /** Field used on the x axis. */
  xKey: keyof Row & string;
  xLabel?: string;
  /** Format the x value for axis ticks and the table. */
  formatX?: (value: unknown) => string;
  series: Series[];
  height?: number;
  /** Shown instead of an empty plot. */
  emptyHint?: string;
}

function ChartFrame({
  title,
  description,
  children,
  table,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  table: ReactNode;
}) {
  const id = useId();
  return (
    <figure
      className="wdc-chart wd-surface"
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-desc` : undefined}
    >
      <figcaption>
        <h3 id={`${id}-title`} className="wdc-chart__title">
          {title}
        </h3>
        {description ? (
          <p id={`${id}-desc`} className="wd-muted">
            {description}
          </p>
        ) : null}
      </figcaption>
      {children}
      <details className="wdc-chart__table">
        <summary>Show as table</summary>
        {table}
      </details>
    </figure>
  );
}

function DataAsTable<Row extends Record<string, unknown>>({
  data,
  xKey,
  xLabel,
  formatX,
  series,
}: Pick<ChartProps<Row>, 'data' | 'xKey' | 'xLabel' | 'formatX' | 'series'>) {
  const intl = useIntl();
  return (
    <table className="wdc-table">
      <thead>
        <tr>
          <th scope="col">{xLabel ?? xKey}</th>
          {series.map((s) => (
            <th scope="col" key={s.key}>
              {s.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i}>
            <th scope="row">{formatX ? formatX(row[xKey]) : String(row[xKey])}</th>
            {series.map((s) => (
              <td key={s.key} className="wd-tabular">
                {typeof row[s.key] === 'number'
                  ? intl.formatNumber(row[s.key] as number)
                  : String(row[s.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const axisProps = { stroke: CHART_COLORS.text, fontSize: 12, tickLine: false } as const;

/**
 * recharts ≥3 turns its accessibility layer on by default, which makes the SVG surface `tabindex="0"` with
 * keyboard handlers. Inside an `aria-hidden` wrapper that is an axe `aria-hidden-focus` violation (E2E-02), and
 * the layer would only narrate what the data table already says. Off, and the surface is explicitly unfocusable.
 */
export const SURFACE_PROPS = { accessibilityLayer: false, tabIndex: -1 } as const;

export function TrendChart<Row extends Record<string, unknown>>(props: ChartProps<Row>) {
  const { title, description, data, xKey, formatX, series, height = 220, emptyHint } = props;
  return (
    <ChartFrame title={title} description={description} table={<DataAsTable {...props} />}>
      {data.length === 0 ? (
        <NoDataYet what="trend" hint={emptyHint} />
      ) : (
        <div style={{ height }} aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} {...SURFACE_PROPS}>
              <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey={xKey as string}
                tickFormatter={formatX ? (v) => formatX(v) : undefined}
                {...axisProps}
              />
              <YAxis allowDecimals={false} width={40} {...axisProps} />
              <Tooltip
                contentStyle={{
                  background: 'var(--wd-surface-elevated)',
                  border: `1px solid ${CHART_COLORS.grid}`,
                  borderRadius: 8,
                }}
              />
              {series.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={CHART_COLORS[s.color ?? 'primary']}
                  fill={CHART_COLORS[s.color ?? 'primary']}
                  fillOpacity={0.18}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartFrame>
  );
}

export function BarsChart<Row extends Record<string, unknown>>(props: ChartProps<Row>) {
  const { title, description, data, xKey, formatX, series, height = 220, emptyHint } = props;
  return (
    <ChartFrame title={title} description={description} table={<DataAsTable {...props} />}>
      {data.length === 0 ? (
        <NoDataYet what="breakdown" hint={emptyHint} />
      ) : (
        <div style={{ height }} aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} {...SURFACE_PROPS}>
              <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey={xKey as string}
                tickFormatter={formatX ? (v) => formatX(v) : undefined}
                {...axisProps}
              />
              <YAxis allowDecimals={false} width={40} {...axisProps} />
              <Tooltip
                contentStyle={{
                  background: 'var(--wd-surface-elevated)',
                  border: `1px solid ${CHART_COLORS.grid}`,
                  borderRadius: 8,
                }}
              />
              {series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={CHART_COLORS[s.color ?? 'primary']}
                  radius={[6, 6, 0, 0]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartFrame>
  );
}
